import { computed, readonly, ref, watch } from "vue"
import { useExploreStore } from "@/explore/ExploreStore"
import { useViewScopeStore } from "../ViewScopeStore"
import { AncestorFamily, FamilyAndChildren, useViewFamilyStore } from "@/rd/ViewFamilyStore"
import { useViewPersonStore } from "@/rd/ViewPersonStore"
import { HighlightItem } from "./HighlightItem"
import { ViewFamily, ViewPerson } from "@/rd/ResearchDataModel"
import { groupBy } from "lodash"
import { FuzzyDatePrecision } from "@/rd/FuzzyDate"
import { HistoricalDateType } from "@/rd/HistoricalDateEnums"
import { LoadMode } from "@/util/AsyncData"

interface Tuplet extends AncestorFamily {
    tuplets: ViewPerson[]
}

const tupletNames = [
    undefined,
    undefined,
    'twins',
    'triplets',
    'quadruplets',
    'quintuplets',
]

function getTupletName(count: number) {
    return tupletNames.at(count) ?? 'tuplets'
}

const twinsIcon = new URL('@/assets/icons/twins_gray_60.png', import.meta.url).href

export function useFamilyHighlights() {
    const exploreStore = useExploreStore()
    const viewScopeStore = useViewScopeStore()
    const viewPersonStore = useViewPersonStore()
    const viewFamilyStore = useViewFamilyStore()

    const scopeInfoRef = computed(() => viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data)
    const treeHighlightsRef = ref<HighlightItem[]>([])

    watch(scopeInfoRef, () => {
        treeHighlightsRef.value = []

        if (!scopeInfoRef.value?.startPersonId)
            return

        const found = [] as HighlightItem[]

        console.log('FamilyHighlights: loading')
        let searchCount = 0

        for (const af of viewFamilyStore.getLoadedAncestorFamilies(scopeInfoRef.value.startPersonId, 100)) {
            searchCount++

            const children = viewFamilyStore.getLoadedChildren(af.family.id!).children
            found.push(...getFamilyHighlights(af.family, children, af.directChildId))

            for (const parentId of af.family.spouseIds) {
                const parent = viewPersonStore.getLoadedPerson(parentId)
                if (parent) {
                    const parentSpouseFamilies = viewFamilyStore.getLoadedSpouseFamilies(parentId).spouseFamilies
                    const familiesAndChildren = parentSpouseFamilies.map(sf => {
                        const children = viewFamilyStore.getLoadedChildren(sf.id!).children
                        return { family: sf, childIds: children.map(ch => ch.id) } as FamilyAndChildren
                    })
                    found.push(...getParentHighlights(parent, familiesAndChildren))
                }
            }
        }

        const selfTupletCount = found.filter(h => h.id.startsWith('selfTuplet:')).length
        const siblingTupletCount = found.filter(h => h.id.startsWith('siblingTuplet:')).length
        if (selfTupletCount + siblingTupletCount > 0) {
            found.push({
                id: 'allTuplets',
                text: 'direct ancestors have twins in their family',
                value: selfTupletCount + siblingTupletCount,
            })

            if (selfTupletCount > 0) {
                found.push({
                    id: 'directTuplets',
                    text: 'direct ancestors have a twin',
                    value: selfTupletCount,
                })
            }
        }

        treeHighlightsRef.value = found
    }, { immediate: true })

    return {
        treeHighlightsRef: readonly(treeHighlightsRef),
    }
}

export function getFamilyHighlights(vf: Readonly<ViewFamily>, children: Readonly<ViewPerson>[], childId: string) {
    const child = children.find(ch => ch.id == childId)
    if (!child)
        throw new Error(`child ${childId} not found in children of family ${vf.id}`)

    const found = [] as HighlightItem[]
    if (children.length >= 10) {
        found.push(toManySiblingsHighlight(child, children.length))
    }

    const childrenByBday = groupBy(children, ch => ch.displayProperties.birthSortDate.value)
    const tupletsInFamily = [] as Tuplet[]
    for (const bday in childrenByBday) {
        const sameBdayChildren = childrenByBday[bday]
        if (sameBdayChildren.length > 1) {
            const sortDate = sameBdayChildren[0].displayProperties.birthSortDate
            if (!sortDate.isEmpty && sortDate.type == HistoricalDateType.Date && sortDate.date1.precision == FuzzyDatePrecision.Day) {
                const tuplet = { family: vf, directChildId: childId, tuplets: sameBdayChildren }
                found.push(toTupletsHighlight(child, tuplet))
                tupletsInFamily.push(tuplet)
            }
        }
    }

    if (tupletsInFamily.length > 1) {
        found.push(toManyTupletsHighlight(child, tupletsInFamily))
    }
    return found
}

export function getParentHighlights(parent: Readonly<ViewPerson>, spouseFamilies: FamilyAndChildren[]) {
    const found = [] as HighlightItem[]
    const uniqueChildIds = new Set(spouseFamilies.flatMap(sf => sf.childIds))
    if (uniqueChildIds.size >= 10) {
        console.log(`FamilyHighlights: found parent with ${uniqueChildIds.size} children`)
        found.push(toManyChildrenHighlight(parent, uniqueChildIds.size))
    }
    return found
}

function toManyChildrenHighlight(parent: Readonly<ViewPerson>, childCount: number) {
    return {
        id: `manyChildren:${parent.id}`,
        personId: parent.id,
        text: `children of ${parent?.displayName}`,
        value: childCount,
    } as HighlightItem
}

function toManySiblingsHighlight(vp: Readonly<ViewPerson>, siblingCount: number) {
    return {
        id: `manySiblings:${vp.id}`,
        personId: vp.id,
        text: `siblings in ${vp?.displayName}'s family`,
        value: siblingCount,
    } as HighlightItem
}

function toTupletsHighlight(child: Readonly<ViewPerson>, t: Tuplet) {
    const selfTuplet = t.tuplets.find(ch => ch.id == t.directChildId)
    const prefix = selfTuplet ? 'selfTuplet' : 'siblingTuplet'
    const text = selfTuplet
        ? (t.tuplets.length > 2
            ? `${child.displayName} and ${t.tuplets.length - 1} siblings are ${getTupletName(t.tuplets.length)}`
            : `${child.displayName} has a twin`)
        : `${child.displayName} has siblings who are ${getTupletName(t.tuplets.length)}`

    return {
        id: `${prefix}:${t.directChildId}`,
        personId: child.id,
        text,
        image: twinsIcon,
    } as HighlightItem
}

function toManyTupletsHighlight(child: Readonly<ViewPerson>, tuplets: Tuplet[]) {
    const tupletDesc = tuplets.every(t => t.tuplets.length == 2) ? 'twins' : 'tuplets'
    return {
        id: `manyTuplets:${child.id}`,
        personId: child.id,
        text: `${child.displayName} has ${tuplets.length} sets of ${tupletDesc} in their family`,
        image: twinsIcon,
    }
}
